import type { MonkeysLocale } from "./types";

export const zhCN = {
  locale: "zh-CN",
  messages: {
    pagination: {
      navigation: "分页导航",
      first: "首页",
      previous: "上一页",
      next: "下一页",
      last: "末页",
      page: ({ page, pageCount }) => `第 ${page} 页，共 ${pageCount} 页`,
      currentPage: ({ page, pageCount }) =>
        `第 ${page} 页，共 ${pageCount} 页，当前页`,
      emptyResults: "共 0 条",
      range: ({ start, end, total }) => `第 ${start}-${end} 条，共 ${total} 条`,
      selected: ({ count }) => `已选择 ${count} 项`,
      pageSize: "每页显示数量",
      perLoad: "每次加载",
      jumpToPage: "跳转到页码",
      pages: "页",
      clickToJump: "点击跳转页码",
    },
    dialog: {
      close: "关闭对话框",
    },
    dropdownMenu: {
      actions: "操作",
    },
    loading: {
      spinner: "正在加载",
    },
    layout: {
      resizePanels: "调整面板大小",
    },
    steps: {
      label: "进度",
      current: "当前步骤",
      completed: "已完成",
      error: "错误",
      disabled: "已禁用",
    },
    numberInput: {
      increment: "增加数值",
      decrement: "减少数值",
    },
    passwordInput: {
      showPassword: "显示密码",
      hidePassword: "隐藏密码",
    },
    multiSelect: {
      placeholder: "请选择",
      empty: "暂无选项",
      removeOption: ({ value }) => `移除 ${value}`,
    },
    table: {
      dragColumn: ({ columnId }) => `拖动列 ${columnId}`,
      resizeColumn: ({ columnId }) => `调整列宽 ${columnId}`,
      sortColumn: ({ columnId }) =>
        columnId ? `排序列 ${columnId}` : "排序列",
      loadingRow: "正在加载表格行",
    },
    toolbar: {
      theme: {
        label: "外观",
        toggle: "切换主题",
        auto: "跟随系统",
        light: "浅色模式",
        dark: "深色模式",
      },
      language: {
        label: "语言",
        switch: "切换语言",
      },
    },
    toast: {
      errorTitle: "错误",
      warningTitle: "提示",
      defaultErrorDescription: "发生错误，请稍后重试。",
      defaultWarningDescription: "请检查后重试。",
    },
    dataExplorer: {
      viewTree: {
        actions: "更多操作",
        expand: "展开",
        collapse: "收起",
        loading: "正在加载...",
        empty: "暂无视图",
        error: "无法加载视图",
      },
    },
    workbench: {
      collection: {
        label: "内容集合",
        loading: "正在加载内容",
        empty: "暂无内容",
        loadError: "无法加载内容",
        refreshError: "无法刷新内容",
        retry: "重试",
        loadingMore: "正在加载更多内容",
        permission: "你没有权限查看此内容",
        offline: "网络连接已断开",
        requestAccess: "申请权限",
      },
      interactiveTable: {
        columnActions: "列操作",
        freezeColumn: "固定到左侧",
        freezeEndColumn: "固定到右侧",
        unfreezeColumn: "取消左侧固定",
        unfreezeEndColumn: "取消右侧固定",
        unfreezeAllColumns: "取消全部固定",
        resetColumnWidth: "重置列宽",
        frozenColumnTitle: "已固定列",
      },
    },
    agentWorkbench: {
      label: "会话活动",
      reasoning: "思考过程",
      plan: "执行计划",
      taskNoDetail: "暂无更多详情",
      modes: { chat: "对话", work: "工作" },
      composer: {
        send: "发送消息",
        stop: "停止生成",
        queue: "排队发送",
        attach: "添加照片或文件",
        voice: "开始语音输入",
        webSearch: "联网搜索",
        chatMode: "对话模式",
        model: "模型",
        reasoning: "思考强度",
        permissions: "权限",
        removeAttachment: "移除附件",
        editQueued: "编辑排队消息",
        deleteQueued: "删除排队消息",
        moveUp: "上移消息",
        moveDown: "下移消息",
        queued: "排队消息",
        uploading: "上传中",
        uploadFailed: "上传失败",
        retryAttachment: "重试附件上传",
        noModels: "暂无可用模型",
        message: "消息",
        attachmentOnly: "附件",
        reasoningOptions: {
          low: "低",
          medium: "中",
          high: "高",
          xhigh: "最高",
        },
        permissionOptions: {
          "approval-required": "需要批准",
          "auto-approve": "自动批准",
          "full-access": "完全访问",
        },
      },
      quickStart: {
        label: "快速开始",
        back: "返回",
        addFile: "添加文件",
      },
      tool: {
        input: "输入",
        output: "输出",
        status: {
          pending: "等待中",
          running: "运行中",
          completed: "已完成",
          failed: "错误",
          denied: "已拒绝",
        },
      },
      approval: {
        approve: "允许",
        reject: "拒绝",
        approved: "已允许",
        rejected: "已拒绝",
      },
      artifact: {
        pending: "准备中",
        ready: "已就绪",
        failed: "失败",
        open: ({ name }) => `打开 ${name}`,
      },
      run: {
        stopping: "正在停止...",
        stoppedAfter: ({ duration }) => `你在 ${duration} 后停止了任务`,
      },
      process: {
        running: "执行过程",
        queued: "排队中",
        waitingApproval: "等待审批",
        failed: "失败",
        stopped: "已停止",
        processed: "已处理",
        processedFor: ({ duration }) =>
          `耗时 ${duration
            .replace(/(\d+)h/g, "$1小时")
            .replace(/(\d+)m/g, "$1分钟")
            .replace(/(\d+)s/g, "$1秒")
            .replace(/\s+/g, "")}`,
        completedCount: ({ completed, total }) =>
          `${completed}/${total} 已完成`,
      },
      artifactWorkspace: {
        outputs: "输出",
        sources: "来源",
        viewAll: "查看全部",
        toggleSummary: "切换文件摘要",
        tabs: "已打开文件",
        closeTab: ({ name }) => `关闭 ${name}`,
        closeWorkspace: "关闭工作区",
        download: ({ name }) => `下载 ${name}`,
        openExternal: ({ name }) => `在新窗口打开 ${name}`,
        loading: "正在加载预览...",
        failed: "预览不可用",
        retry: "重试",
      },
      usage: {
        label: "上下文用量",
        context: ({ used, total }) => `${used} / ${total} tokens`,
      },
      navigation: {
        search: "搜索智能体和会话",
        searchSessions: "搜索会话",
        searchShortcut: "聚焦搜索框",
        clearSearch: "清除搜索",
        tabsLabel: "智能体工作台导航",
        agents: "智能体",
        sessions: "会话",
        newAgent: "新建智能体",
        newSession: "新建会话",
        capabilities: "能力与扩展",
        settings: "智能体设置",
        allAgents: "全部智能体",
        recentSessions: "最近会话",
        chat: "对话",
        work: "工作",
        chats: "聊天",
        builtIn: "内置",
        noDescription: "暂无描述",
        loading: "加载中...",
        emptyAgents: "暂无智能体",
        emptySessions: "暂无会话",
        noResults: "没有匹配结果",
        retry: "重试",
        pin: "置顶",
        unpin: "取消置顶",
        rename: "重命名",
        delete: "删除",
        actions: "操作",
        save: "保存",
        cancel: "取消",
        running: "运行中",
        completed: "已完成",
        failed: "失败",
        contextUsage: ({ percent }) => `已使用 ${percent}% 上下文`,
      },
      details: {
        title: "任务详情",
        description: "摘要与工作区活动",
        duration: "耗时",
        updated: "更新时间",
        model: "模型",
        mode: "模式",
        permissions: "权限",
        tokenUsage: "Token 用量",
        summary: "摘要",
        summaryEmpty: "暂时没有可用摘要。",
        summaryFailed: "摘要生成失败。",
        highlights: "重点",
        pendingItems: "待处理事项",
        tabs: {
          label: "任务详情视图",
          summary: "摘要",
          workspace: "工作区",
        },
        status: {
          queued: "排队中",
          running: "运行中",
          waiting_approval: "等待审批",
          stopping: "正在停止",
          stopped: "已停止",
          completed: "已完成",
          failed: "失败",
        },
        state: {
          loading: {
            title: "正在加载任务详情",
            description: "正在恢复最新的会话活动。",
          },
          empty: {
            title: "未选择任务",
            description: "选择任务后查看详细信息。",
          },
          error: {
            title: "无法加载任务详情",
            description: "会话恢复可用后请重试。",
          },
        },
        actions: {
          close: "关闭任务详情",
          copySummary: "复制摘要",
          retry: "重试",
          retrySummary: "重新生成摘要",
          resume: "继续运行",
          continueInNewTask: "在新任务中继续",
          editAndRerun: "编辑并重新运行",
          export: "导出",
        },
        workspace: {
          diffs: "代码变更",
          files: "文件",
          terminals: "终端",
          tests: "测试",
          artifacts: "产物",
          exitCode: ({ code }) => `退出码 ${code}`,
          diffStatus: {
            added: "新增",
            modified: "修改",
            deleted: "删除",
            renamed: "重命名",
          },
          fileOperation: { upsert: "已更新", delete: "已删除" },
        },
      },
    },
  },
} satisfies MonkeysLocale;
